import { TenantViewModel } from '@ail/customers-api'
import {
  OrderInternalViewModel,
  OrderSuppliersViewModel,
  WorkflowConfigInputModel,
  WorkflowConfigViewModel,
  WorkflowOrderViewModel,
} from '@ail/orders-api'
import React, { Component } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { ConnectedProps, connect } from 'react-redux'
import { NavLink, RouteComponentProps, withRouter } from 'react-router-dom'
import { downloadFile } from '../../../../helpers/FileDownload'
import { wrapQuery } from '../../../../redux/api/apiQuery'
import { customersApi } from '../../../../redux/api/customerApi'
import { ordersApi } from '../../../../redux/api/ordersApi'
import { emptyGuid, newItemId } from '../../../../types/constants'
import { WorkflowOrderStepStatus, WorkflowOrderType } from '../../../../types/enums'
import Alert from '../../../common/Alert/Alert'
import Modal from '../../../common/Modal/Modal'
import { Loader } from '../../../common/Loader/Loader'

const newProcessId = 'new'

interface IAlertState {
  visible: boolean
  message: string
  title: string
}

type EditSupplierWorkflowsProps = ConnectedProps<typeof connector> &
  WithTranslation &
  RouteComponentProps<{ id: string }>

interface IState {
  tenants: TenantViewModel[]
  workflowConfig?: WorkflowConfigViewModel
  recreateOrder?: WorkflowOrderViewModel
  ordersToRecreate?: OrderSuppliersViewModel[] | OrderInternalViewModel[]
  recreateStatus: WorkflowOrderStepStatus
  workflowConfigString?: string
  jsonChanged: boolean
  savingError: IAlertState
  loading: boolean
}

class EditSupplierWorkflowForm extends Component<EditSupplierWorkflowsProps, IState> {
  constructor(props: EditSupplierWorkflowsProps) {
    super(props)
    this.state = {
      tenants: [],
      recreateOrder: undefined,
      ordersToRecreate: undefined,
      recreateStatus: WorkflowOrderStepStatus.New,
      jsonChanged: false,
      savingError: {
        message: 'nothing',
        title: 'nothing',
        visible: false,
      },
      loading: false,
    }
  }

  componentDidMount() {
    const workflowId = this.props.match.params.id

    this.setState({
      loading: true,
    })

    this.props.loadTenants().then(
      (tenants) => {
        this.setState({ tenants })
        if (workflowId !== newItemId) {
          this.props.getWorkflowConfig(workflowId).then(
            (workflowConfig) => {
              this.setState({
                loading: false,
                workflowConfig,
                workflowConfigString: JSON.stringify(workflowConfig, null, '\t'),
              })
            },
            () => {
              this.setState({
                loading: false,
                workflowConfig: undefined,
              })
            }
          )
        } else {
          const workflowConfig = {
            id: emptyGuid,
            name: 'New Workflow',
            orders: [],
          }
          this.setState({
            loading: false,
            workflowConfig,
            workflowConfigString: JSON.stringify(workflowConfig, null, '\t'),
          })
        }
      },
      () => this.setState({ loading: false })
    )
  }

  public render() {
    const {
      workflowConfigString,
      workflowConfig,
      savingError,
      loading,
      recreateOrder,
      recreateStatus,
      ordersToRecreate,
    } = this.state

    return (
      <Loader blocking={loading} style={{ overflow: 'hidden' }}>
        <div className="workflow row">
          <div className="ordersArea newFormArea col-5" style={{ minWidth: '0' }}>
            <div className="ordersFormArea">
              <div className="detailTitle">
                <i className="icon-edit" />
                <span>Edit Workflow</span>
              </div>
            </div>
            <div className="request-form-edit">
              <label>Enter new approval process json</label>
              <textarea onChange={(e) => this.handleJsonChange(e.target.value)} value={workflowConfigString} />
              <div className="apply-btn">
                {this.state.jsonChanged && <button onClick={() => this.applyJson()}>Apply JSON</button>}
              </div>
            </div>
            <div className="btnArea">
              <NavLink to="/RequestsManagement/SupplierWorkflows" className="btn btnClose">
                <i className="icon-close" /> CANCEL
              </NavLink>
              <a onClick={(e) => this.submit(e)} className="btn btnSave">
                <i className="icon-check" /> SUBMIT
              </a>
            </div>
            <Alert
              onClose={() => {
                this.closeSavingErrorAlert()
              }}
              visible={savingError.visible}
              title={savingError.title}
              message={savingError.message}
            />
          </div>
          {workflowConfig && (
            <div className="workflowSteps col-4">
              <div className="detailTitle">
                <i className="icon-check" />
                <span>Orders</span>
              </div>
              <div className="ordersContent">
                {workflowConfig.orders?.map((order) => (
                  <ul>
                    <li>
                      <span
                        style={{
                          borderLeftColor: this.checkTenant(order.tenantId ?? '') ? '#43B54A' : 'red',
                        }}
                      >
                        {order.form.name}
                        <i className={this.checkTenant(order.tenantId ?? '') ? 'icon-check' : 'icon-warning'} />
                        &nbsp;&nbsp;
                        {this.props.match.params.id !== newItemId && (
                          <a onClick={() => this.openOrderRecreate(order)}>
                            <i title="Recreate orders" className="icon-reload" />
                          </a>
                        )}
                      </span>
                      <ul>
                        <li>
                          <ul>
                            {this.checkTenant(order.tenantId ?? '') ? (
                              <li className="valid-approver" style={{ marginBottom: 15 }}>
                                {`Tenant ${order.tenantId} found`}
                              </li>
                            ) : (
                              <>
                                {order.tenantId ? (
                                  <li className="notFound" style={{ marginBottom: 15 }}>
                                    {`Tenant ${order.tenantId} not found`}
                                  </li>
                                ) : (
                                  <li className="notFound" style={{ marginBottom: 15 }}>
                                    {`Tenant not specified`}
                                  </li>
                                )}
                              </>
                            )}
                          </ul>
                        </li>
                      </ul>
                    </li>
                  </ul>
                ))}
              </div>
            </div>
          )}
        </div>
        <Modal
          title={`Recreate orders - ${recreateOrder?.form.name}`}
          visible={!!recreateOrder}
          onClose={() => this.closeOrderRecreate()}
        >
          <label>Recreate orders with status:</label>
          <div className="selectArea">
            <select value={recreateStatus} onChange={(e) => this.setRecreateStatus(e.target.value)}>
              {Object.keys(WorkflowOrderStepStatus).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <i className="icon-bottom" />
          </div>
          <br />
          {this.recreateContent()}
          <div className="modal-bottom">
            {!ordersToRecreate ? (
              <button className="btn disabled" onClick={() => this.searchOrdersToRecreate()}>
                Continue
              </button>
            ) : (
              <>
                <button className="btn disabled" onClick={() => this.recreateOrders()}>
                  Recreate orders
                </button>
                <button className="btn disabled" onClick={() => this.exportRecreateOrders()}>
                  <i className="icon-excel" /> Export
                </button>
              </>
            )}
          </div>
        </Modal>
      </Loader>
    )
  }

  private searchOrdersToRecreate() {
    const { recreateStatus, recreateOrder, workflowConfig } = this.state
    if (!recreateOrder || !workflowConfig || workflowConfig.id === newItemId) {
      return
    }

    const getWorkflowOrders =
      recreateOrder.type === WorkflowOrderType.Internal
        ? this.props.getWorkflowOrdersInternal
        : this.props.getWorkflowOrdersSuppliers

    this.setState({ loading: true })

    getWorkflowOrders(workflowConfig.id).then(
      (orders) => {
        const ordersToRecreate = orders.filter((o) => {
          const tenantId = 'tenantId' in o ? o.tenantId : undefined
          return o.form.status === recreateStatus && (!tenantId || tenantId === recreateOrder.tenantId)
        })

        this.setState({
          ordersToRecreate,
          loading: false,
        })
      },
      () => this.setState({ loading: false })
    )
  }

  private recreateContent() {
    const { ordersToRecreate, recreateOrder, recreateStatus } = this.state
    if (!ordersToRecreate) {
      return <></>
    }
    if (ordersToRecreate.length) {
      return (
        <div>
          {ordersToRecreate.length} <b>{recreateOrder?.form.name}</b> orders with status <b>{recreateStatus}</b> were
          found!
          <br />
          <br />
          Are you sure to recreate{' '}
          <b>
            {ordersToRecreate.length} {recreateOrder?.form.name}
          </b>{' '}
          orders?
        </div>
      )
    } else {
      return (
        <div>
          <b>{recreateOrder?.form.name}</b> orders with status <b>{recreateStatus}</b> were not found!
        </div>
      )
    }
  }

  private setRecreateStatus(status: string) {
    this.setState({
      recreateStatus: status as WorkflowOrderStepStatus,
      ordersToRecreate: undefined,
    })
  }

  private exportRecreateOrders() {
    const { ordersToRecreate, recreateOrder } = this.state
    const headerLine = 'Id,LogisticId\n'
    const orderLines = ordersToRecreate?.map((o) => `${o.id},${o.logisticId ?? ''}`).join('\n')
    const content = headerLine + orderLines
    const file = new Blob([content], { type: 'text/csv' })
    const fileURL = URL.createObjectURL(file)
    const filename = `${recreateOrder?.form.name}.csv`

    downloadFile({
      fileURL,
      filename,
    })
  }

  private recreateOrders() {
    const { workflowConfig, recreateOrder, recreateStatus } = this.state
    if (!workflowConfig || !recreateOrder) return

    this.setState({ loading: true })

    if (recreateOrder.type === WorkflowOrderType.Internal) {
      this.props
        .recreateWorkflowOrdersInternal(workflowConfig.id, recreateStatus)
        .then(() => {
          this.setState({
            ordersToRecreate: undefined,
            recreateStatus: WorkflowOrderStepStatus.New,
            recreateOrder: undefined,
          })
          setTimeout(() => {
            alert('Orders recreated!')
          }, 100)
        })
        .finally(() => this.setState({ loading: false }))
    } else if (recreateOrder.tenantId) {
      this.props
        .recreateWorkflowOrdersSuppliers(workflowConfig.id, recreateOrder.tenantId, recreateStatus)
        .then(() => {
          this.setState({
            ordersToRecreate: undefined,
            recreateStatus: WorkflowOrderStepStatus.New,
            recreateOrder: undefined,
          })
          setTimeout(() => {
            alert('Orders recreated!')
          }, 100)
        })
        .finally(() => this.setState({ loading: false }))
    }
  }

  private closeOrderRecreate() {
    this.setState({
      recreateOrder: undefined,
      ordersToRecreate: undefined,
      recreateStatus: WorkflowOrderStepStatus.New,
    })
  }

  private openOrderRecreate(order: WorkflowOrderViewModel) {
    this.setState({
      recreateOrder: order,
      ordersToRecreate: undefined,
      recreateStatus: WorkflowOrderStepStatus.New,
    })
  }

  private checkTenant(tenantId: string) {
    return this.state.tenants.some((t) => t.id === tenantId)
  }

  private handleJsonChange(workflowConfigString: string) {
    this.setState({ workflowConfigString, jsonChanged: true })
  }

  private closeSavingErrorAlert() {
    const savingError: IAlertState = {
      ...this.state.savingError,
      visible: !this.state.savingError.visible,
    }
    this.setState({
      savingError,
    })
  }

  private applyJson(): WorkflowConfigViewModel | null {
    try {
      const workflowConfig = JSON.parse(this.state.workflowConfigString ?? '') as WorkflowConfigViewModel

      const duplicateMap: Record<string, Record<string, boolean>> = {}

      for (const order of workflowConfig.orders) {
        const duplicate = order.form.fields.find((f) => order.form.fields.filter((df) => f.name === df.name).length > 1)
        if (duplicate) {
          duplicateMap[order.form.name] = {
            ...duplicateMap[order.form.name],
            [duplicate.name]: true,
          }
        }
      }

      const duplicates = Object.keys(duplicateMap)

      if (duplicates.length) {
        const message = duplicates.map((k) => `${k}: ${Object.keys(duplicateMap[k]).join(', ')}`).join('\n')
        alert(`Duplicated fields found. \n\n${message}`)
        return null
      } else {
        this.setState({
          workflowConfig,
          jsonChanged: false,
        })
      }

      return workflowConfig
    } catch (error) {
      alert(error)
      return null
    }
  }

  private navigateToGrid(e?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    if (e) {
      e.preventDefault()
    }
    const { history } = this.props
    history.push('/RequestsManagement/SupplierWorkflows')
  }

  private submit(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    e.preventDefault()

    const currentProcess = this.applyJson()

    if (currentProcess) {
      const model: WorkflowConfigInputModel = {
        ...currentProcess,
      }
      this.setState({
        loading: true,
      })
      const promise =
        this.props.match.params.id === newProcessId
          ? this.props.postWorkflowConfig(model)
          : this.props.putWorkflowConfig(currentProcess.id, model)

      promise.then(
        (savedProcess) => {
          alert(`Approval process saved! Process ID ${savedProcess.id}`)
          this.navigateToGrid()
          this.setState({
            loading: false,
          })
        },
        () => {
          // alert('Approval process saving failed!')
          this.setState({
            loading: false,
            savingError: {
              visible: true,
              message: this.props.t('ERROR_ON_SAVING_FORM'),
              title: this.props.t('ERROR'),
            },
          })
        }
      )
    }
  }
}

function mapStateToProps() {
  return {}
}

const mapDispatchToProps = {
  postWorkflowConfig: wrapQuery((model: WorkflowConfigInputModel) =>
    ordersApi.endpoints.postWorkflowConfig.initiate(model)
  ),
  putWorkflowConfig: wrapQuery((id: string, model: WorkflowConfigInputModel) =>
    ordersApi.endpoints.putWorkflowConfig.initiate({ id, model })
  ),
  getWorkflowConfig: wrapQuery((id: string) => ordersApi.endpoints.getWorkflowConfig.initiate(id)),
  getWorkflowOrdersSuppliers: wrapQuery((configId: string) =>
    ordersApi.endpoints.getWorkflowOrdersSuppliers.initiate(configId)
  ),
  getWorkflowOrdersInternal: wrapQuery((configId: string) =>
    ordersApi.endpoints.getWorkflowOrdersInternal.initiate(configId)
  ),
  recreateWorkflowOrdersSuppliers: wrapQuery((configId: string, tenantId: string, status?: WorkflowOrderStepStatus) =>
    ordersApi.endpoints.recreateOrdersSuppliers.initiate({ configId, tenantId, status })
  ),
  recreateWorkflowOrdersInternal: wrapQuery((configId: string, status?: WorkflowOrderStepStatus) =>
    ordersApi.endpoints.recreateOrdersInternal.initiate({ configId, status })
  ),
  loadTenants: wrapQuery(() => customersApi.endpoints.getTenants.initiate()),
}
const connector = connect(mapStateToProps, mapDispatchToProps)
// eslint-disable-next-line react-refresh/only-export-components
export default connector(withTranslation('common')(withRouter(EditSupplierWorkflowForm)))
